import { z } from 'zod';
import { TimeFrame } from './webapp-common.dto';

export const BasicOverviewQuery = z.object({
  timeFrame: TimeFrame.optional(), // default is 'week'
  environmentName: z.string().optional(),
  serviceIds: z.array(z.coerce.number()).optional(),
});
export type BasicOverviewQuery = z.infer<typeof BasicOverviewQuery>;

export const GetDiscoveryQuery = BasicOverviewQuery;
export type GetDiscoveryQuery = z.infer<typeof GetDiscoveryQuery>;

const FunctionEntity = z.object({
  id: z.number(),
  signature: z.string(),
});

const EndpointEntity = z.object({
  id: z.number(),
  path: z.string(),
  serviceId: z.number(),
  serviceName: z.string(),
});

const QueueEntity = z.object({
  id: z.number(),
  queueName: z.string(),
  queueType: z.string(),
  serviceId: z.number(),
  serviceName: z.string(),
});

const DurationMetric = z.object({
  duration: z.number(),
  runs: z.number(),
});

const DurationTrendMetric = z.object({
  duration: z.number().nullable(),
  trend: z.number().nullable(),
});

const ErrorRateMetric = z.object({
  errorRate: z.number(),
});

const ErrorRateTrendMetric = z.object({
  errorRate: z.number().nullable(),
  trend: z.number().nullable(),
});

const InvocationsMetric = z.object({
  runs: z.number(),
});

const InvocationsTrendMetric = z.object({
  runs: z.number().nullable(),
  trend: z.number().nullable(),
});

const duration = z.object({
  functions: z.array(FunctionEntity.merge(DurationMetric)),
  endpoints: z.array(EndpointEntity.merge(DurationMetric)),
  queues: z.array(QueueEntity.merge(DurationMetric)),
});

const durationTrend = z.object({
  functions: z.array(FunctionEntity.merge(DurationTrendMetric)),
  endpoints: z.array(EndpointEntity.merge(DurationTrendMetric)),
  queues: z.array(QueueEntity.merge(DurationTrendMetric)),
});

const errorRate = z.object({
  functions: z.array(FunctionEntity.merge(ErrorRateMetric)),
  endpoints: z.array(EndpointEntity.merge(ErrorRateMetric)),
  queues: z.array(QueueEntity.merge(ErrorRateMetric)),
});

const errorRateTrend = z.object({
  functions: z.array(FunctionEntity.merge(ErrorRateTrendMetric)),
  endpoints: z.array(EndpointEntity.merge(ErrorRateTrendMetric)),
  queues: z.array(QueueEntity.merge(ErrorRateTrendMetric)),
});

const invocations = z.object({
  functions: z.array(FunctionEntity.merge(InvocationsMetric)),
  endpoints: z.array(EndpointEntity.merge(InvocationsMetric)),
  queues: z.array(QueueEntity.merge(InvocationsMetric)),
});

const invocationsTrend = z.object({
  functions: z.array(FunctionEntity.merge(InvocationsTrendMetric)),
  endpoints: z.array(EndpointEntity.merge(InvocationsTrendMetric)),
  queues: z.array(QueueEntity.merge(InvocationsTrendMetric)),
});

export const DiscoveryResponse = z.object({
  duration,
  durationTrend,
  errorRate,
  errorRateTrend,
  invocations,
  invocationsTrend,
});

export type FunctionEntitySchema = z.infer<typeof FunctionEntity>;
export type EndpointEntitySchema = z.infer<typeof EndpointEntity>;
export type QueueEntitySchema = z.infer<typeof QueueEntity>;

export type EntitySchema = FunctionEntitySchema | EndpointEntitySchema | QueueEntitySchema;

export enum DiscoveryType {
  Duration = 'duration',
  DurationTrend = 'durationTrend',
  ErrorRate = 'errorRate',
  ErrorRateTrend = 'errorRateTrend',
  Invocations = 'invocations',
  InvocationsTrend = 'invocationsTrend',
}
export type DurationDiscovery = z.infer<typeof duration>;
export type DurationTrendDiscovery = z.infer<typeof durationTrend>;
export type ErrorRateDiscovery = z.infer<typeof errorRate>;
export type ErrorRateTrendDiscovery = z.infer<typeof errorRateTrend>;
export type InvocationsDiscovery = z.infer<typeof invocations>;
export type InvocationsTrendDiscovery = z.infer<typeof invocationsTrend>;

export type DiscoveryResponse = z.infer<typeof DiscoveryResponse>;

export type JoinedDiscoveryData =
  | DurationDiscovery
  | DurationTrendDiscovery
  | ErrorRateDiscovery
  | ErrorRateTrendDiscovery
  | InvocationsDiscovery
  | InvocationsTrendDiscovery;

export const GetEntityTotalsQuery = BasicOverviewQuery;
export type GetEntityTotalsQuery = z.infer<typeof GetEntityTotalsQuery>;

export const EntityTotals = z.object({
  entityType: z.enum(['ENDPOINT', 'FUNCTION', 'QUEUE']),
  calledFigure: z.number(),
  mappedFigure: z.number(),
});
export type EntityTotals = z.infer<typeof EntityTotals>;

export const GetEntityTotalsResponse = z.object({
  endpoints: EntityTotals,
  functions: EntityTotals,
  queues: EntityTotals,
});
export type GetEntityTotalsResponse = z.infer<typeof GetEntityTotalsResponse>;